import { merge } from 'es-toolkit';
import {
	io,
	type ManagerOptions,
	type Socket,
	type SocketOptions
} from 'socket.io-client';

import type {
	AnySocketConnectionParams,
	EventKey,
	EventPayload,
	SocketServiceEventMap
} from '../types';
import {
	E2ERealtimeTransport,
	isE2ERealtimeMockEnabled
} from './e2e-realtime.service';

export class SocketService<TEventMap extends SocketServiceEventMap> {
	static create<TEvents extends SocketServiceEventMap>() {
		return isE2ERealtimeMockEnabled()
			? new E2ERealtimeTransport<TEvents>()
			: new SocketService<TEvents>();
	}

	private socket: Socket | null = null;

	private pendingListeners: {
		[K in EventKey<TEventMap>]?: Set<
			(payload: EventPayload<TEventMap, K>) => void
		>;
	} = {};

	private listenerRegistry: {
		[K in EventKey<TEventMap>]?: Set<
			(payload: EventPayload<TEventMap, K>) => void
		>;
	} = {};

	connect(params: AnySocketConnectionParams): Socket {
		if (this.socket) {
			return this.socket;
		}

		const defaultOptions: Partial<ManagerOptions & SocketOptions> = {
			transports: ['websocket'],
			autoConnect: true,
			reconnection: true,
			reconnectionAttempts: Number.POSITIVE_INFINITY,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 10000,
			query: {
				...(params.clientSecret ? { client_secret: params.clientSecret } : {}),
				origin: globalThis.origin,
				external_id: params.externalId
			},
			...(params.token ? { auth: { token: params.token } } : {})
		};

		this.socket = io(
			params.endpoint,
			merge(defaultOptions, params.options || {})
		);

		this.socket.on('disconnect', () => {
			this.socket = null;
		});

		for (const event in this.listenerRegistry) {
			const handlers = this.listenerRegistry[event as EventKey<TEventMap>];
			if (!handlers) {
				continue;
			}

			handlers.forEach((handler) => {
				this.socket?.on(event as string, handler);
			});
		}

		this.pendingListeners = {};

		return this.socket;
	}

	on<K extends EventKey<TEventMap>>(
		event: K,
		handler: (payload: EventPayload<TEventMap, K>) => void
	) {
		const existing = this.listenerRegistry[event] ?? new Set();
		this.listenerRegistry[event] = existing;

		if (existing.has(handler)) {
			return () => {
				this.removeListener(event, handler);
			};
		}

		existing.add(handler);

		if (this.socket) {
			this.socket.on(event as string, handler);
		} else {
			if (!this.pendingListeners[event]) {
				this.pendingListeners[event] = new Set();
			}
			this.pendingListeners[event]?.add(handler);
		}

		return () => {
			this.removeListener(event, handler);
		};
	}

	disconnect() {
		const socket = this.socket;

		this.socket = null;
		this.pendingListeners = {};
		this.listenerRegistry = {};

		if (!socket) {
			return;
		}

		socket.off();
		socket.disconnect();
	}

	getSocket() {
		return this.socket;
	}

	private removeListener<K extends EventKey<TEventMap>>(
		event: K,
		handler: (payload: EventPayload<TEventMap, K>) => void
	): void {
		this.listenerRegistry[event]?.delete(handler);
		this.pendingListeners[event]?.delete(handler);

		if (this.socket) {
			this.socket.off(event as string, handler);
		}

		if (this.listenerRegistry[event]?.size === 0) {
			delete this.listenerRegistry[event];
		}

		if (this.pendingListeners[event]?.size === 0) {
			delete this.pendingListeners[event];
		}
	}
}
